export type ChangeType =
  | 'normal'
  | 'normal_correcao'
  | 'emergencial'
  | 'emergencial_retroativa'
  | 'padrao'
  | (string & {});

export interface ChangeTypePolicyFlags {
  enable_internal_subtasks: boolean;
  enable_external_subtasks: boolean;
}

export interface ChangeTypePolicy extends ChangeTypePolicyFlags {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  is_active?: boolean;
  is_system?: boolean;
  sort_order?: number;
}

export type ChangeStatus =
  | 'avaliar'
  | 'agendado'
  | 'autorizar'
  | 'transporte'
  | 'revisar'
  | 'encerrar'
  | 'cancelada';

export interface ChangeUserLite {
  id: number;
  name: string;
  email: string;
}

export interface ChangeApproval {
  id: number;
  group_id?: number | null;
  group_name?: string | null;
  track?: string | null;
  assignment_kind?: 'setor' | 'pessoa' | null;
  assignee_id?: number | null;
  assignee_name?: string | null;
  approver_id?: number | null;
  approver_name?: string | null;
  status: string;
  justification?: string | null;
  extra_fields?: Record<string, string | null>;
  decided_at?: string | null;
  can_decide?: boolean;
  order_index?: number | null;
  wait_reason?: string | null;
  /** Slot extra de Transporte para mudança SAP com «Tem request?» = Sim. */
  is_sap_request_transport?: boolean;
}

export interface ChangeApprovalPlanArea {
  group_id: number;
  name?: string | null;
}

export interface ChangeApprovalPlanPerson {
  group_id: number;
  group_name?: string | null;
  assignee_id: number;
  assignee_name?: string | null;
}

export interface ChangeApprovalPlanTrack {
  track: string;
  label: string;
  areas: ChangeApprovalPlanArea[];
  people?: ChangeApprovalPlanPerson[];
}

export interface ChangeApprovalPlan {
  tracks: ChangeApprovalPlanTrack[];
}

export interface ChangeApprovalSettingsTrack {
  track: string;
  label: string;
  enabled: boolean;
}

export type CreatorApproverRole = 'criador' | 'gestor';
export type PlanEditorRole = 'admin' | 'gestor' | 'criador' | 'dono' | 'solicitante';
export type PostImplEditorRole = 'admin' | 'gestor' | 'dono' | 'solicitante';
export type ApprovalOrderMode = 'livre' | 'criador_primeiro';

export interface ChangeApprovalSettings {
  enable_riscos_impactos: boolean;
  enable_camud_go_no_go: boolean;
  enable_gestao_mudancas?: boolean;
  creator_approver_roles?: CreatorApproverRole[];
  plan_editor_roles?: PlanEditorRole[];
  post_impl_editor_roles?: PostImplEditorRole[];
  approval_order_mode?: ApprovalOrderMode;
  /** Status em que criador/solicitante podem editar o formulário da mudança. */
  form_editable_statuses?: string[];
  tracks: ChangeApprovalSettingsTrack[];
  form_fields?: ChangeFormField[];
}

export interface ChangeEmergencyStep {
  group_id: number;
  group_name?: string | null;
  kind: 'requester_area' | 'fixed' | string;
  sort_order?: number;
  track?: string;
  source?: 'solicitante_membership' | 'requester_area_map' | 'fixed' | string;
}

export interface RequesterImmediateManagerGroupMember {
  user_id: number;
  name?: string | null;
  email?: string | null;
  is_manager?: boolean;
}

export interface RequesterImmediateManagerGroupOption {
  group_id: number;
  group_name?: string | null;
  members: RequesterImmediateManagerGroupMember[];
}

export interface RequesterImmediateManagerSetup {
  active: boolean;
  needs_assignment?: boolean;
  /** Exibe o configurador em Transporte (obrigatório ou opcional sem cadastro individual). */
  show_setup_panel?: boolean;
  has_individual_immediate_manager?: boolean;
  needs_group_pick?: boolean;
  can_configure?: boolean;
  selected_group_id?: number | null;
  requester_id?: number;
  requester_name?: string | null;
  groups?: RequesterImmediateManagerGroupOption[];
}

export interface ChangeEmergencyPreview {
  requester_area_first: boolean;
  unlock_others_after_first: boolean;
  waiting_area_name?: string | null;
  steps: ChangeEmergencyStep[];
  /** Áreas da cadeia sem gestor (status ficará barrado). */
  missing_managers?: Array<{
    group_id: number;
    group_name?: string | null;
    kind?: string;
    is_first?: boolean;
  }>;
}

export interface ChangeEmergencyFixedArea {
  group_id: number;
  name?: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface ChangeEmergencyManager {
  group_id: number;
  group_name?: string | null;
  user_id: number;
  user_name?: string | null;
  user_email?: string | null;
}

export interface ChangeEmergencyRequesterMap {
  requester_area_id: number;
  requester_area_name?: string | null;
  group_id: number;
  group_name?: string | null;
}

export interface ChangeEmergencyPolicyManagerArea {
  group_id: number;
  name?: string | null;
  has_manager: boolean;
  from_group: Array<{
    user_id: number;
    user_name?: string | null;
    user_email?: string | null;
  }>;
  fixed_managers: Array<{
    user_id: number;
    user_name?: string | null;
    user_email?: string | null;
  }>;
}

export interface ChangeEmergencyPolicy {
  requester_area_first: boolean;
  unlock_others_after_first: boolean;
  fixed_areas: ChangeEmergencyFixedArea[];
  managers: ChangeEmergencyManager[];
  requester_maps: ChangeEmergencyRequesterMap[];
  manager_areas?: ChangeEmergencyPolicyManagerArea[];
}

export interface ChangeTypeApprovalFlowStep {
  group_id: number;
  name?: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface ChangeTypeApprovalFlow {
  change_type_code: string;
  change_type_name?: string | null;
  flow_enabled: boolean;
  use_immediate_manager: boolean;
  parallel_after_first: boolean;
  template_hint?: string | null;
  steps: ChangeTypeApprovalFlowStep[];
}

export interface ChangeTypeApprovalFlowSummary {
  change_type_code: string;
  change_type_name?: string | null;
  flow_enabled: boolean;
  use_immediate_manager: boolean;
  steps_count: number;
}

export interface ChangeApprovalFlowConfigStep {
  group_id: number;
  name?: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface ChangeApprovalFlowConfigManager {
  group_id: number;
  group_name?: string | null;
  user_id: number;
  user_name?: string | null;
  user_email?: string | null;
}

export interface ChangeApprovalFlowConfig {
  id: number;
  name: string;
  change_type_code?: string | null;
  change_type_name?: string | null;
  is_active: boolean;
  use_immediate_manager: boolean;
  parallel_after_first: boolean;
  steps: ChangeApprovalFlowConfigStep[];
  managers: ChangeApprovalFlowConfigManager[];
}

export interface ChangeSapRequestTransportSettings {
  is_enabled: boolean;
  group_id?: number | null;
  group_name?: string | null;
  assignee_id?: number | null;
  assignee_name?: string | null;
  assignee_email?: string | null;
  updated_at?: string | null;
}

export interface ChangeSubtaskAttachment {
  id: number;
  filename: string;
  file_path?: string | null;
  file_size?: number | null;
  uploaded_at?: string | null;
  comment_id?: number | null;
}

export interface ChangeSubtaskRequesterAccess {
  requester_allow_create?: boolean | null;
  requester_allow_edit?: boolean | null;
  requester_allow_delete?: boolean | null;
  effective_allow_create: boolean;
  effective_allow_edit: boolean;
  effective_allow_delete: boolean;
}

export interface ChangeSubtaskRequesterDefaults {
  allow_create: boolean;
  allow_edit: boolean;
  allow_delete: boolean;
}

export interface ChangeSubtask {
  id: number;
  title: string;
  group_id: number;
  internal_template_id?: number | null;
  group_name?: string | null;
  assignment_kind?: 'setor' | 'grupo' | 'pessoa' | null;
  origin?: 'checklist' | 'manual' | 'incidente' | null;
  gates_implementar?: boolean;
  incident_dispatch_pending?: boolean;
  task_type_id?: number | null;
  task_type_name?: string | null;
  incident?: ChangeSubtaskIncident | null;
  incident_outcome?: 'sucesso' | 'insucesso' | null;
  allow_thread?: boolean | null;
  assignee_id?: number | null;
  assignee_name?: string | null;
  ticket_id?: number | null;
  created_by?: number | null;
  created_by_name?: string | null;
  created_at?: string | null;
  responsible_id: number;
  responsible_name?: string | null;
  due_date?: string | null;
  status: string;
  invalidated_at?: string | null;
  invalidation_reason?: string | null;
  comments?: string | null;
  extra_fields?: Record<string, string | null>;
  checklist?: ChangeSubtaskChecklist | null;
  conclusion?: ChangeSubtaskConclusion | null;
  attachments?: ChangeSubtaskAttachment[];
  requester_access?: ChangeSubtaskRequesterAccess;
  can_edit_subtask?: boolean;
  can_delete_subtask?: boolean;
  can_interact_create?: boolean;
  can_manage_requester_access?: boolean;
  can_assume_subtask?: boolean;
}

export interface ChangeSubtaskIncident {
  ticket_id?: number | null;
  status?: string | null;
  current_step?: string | null;
  taken_by_id?: number | null;
  taken_by_name?: string | null;
  outcome?: 'sucesso' | 'insucesso' | null;
}

export type InternalSubtaskFormTabsPosition = 'before' | 'after';

export interface ChangeSubtaskChecklist {
  items: ChangeFormField[];
  answers: Record<string, string | null>;
  tabs?: string[];
  tabs_position?: InternalSubtaskFormTabsPosition;
}

export type ChangeSubtaskConcludeMode = 'assignee' | 'owner' | 'both';

export interface ChangeSubtaskConclusionStamp {
  at?: string | null;
  user_id?: number | null;
  user_name?: string | null;
}

export interface ChangeSubtaskConclusion {
  mode: ChangeSubtaskConcludeMode;
  button_label: string;
  can_conclude: boolean;
  assignee_done?: ChangeSubtaskConclusionStamp | null;
  owner_done?: ChangeSubtaskConclusionStamp | null;
  waiting_message?: string | null;
}

export interface ChangeWorkGroupMember {
  user_id: number;
  name?: string | null;
  email?: string | null;
  is_manager?: boolean;
  immediate_manager_user_id?: number | null;
  immediate_manager_name?: string | null;
  immediate_manager_email?: string | null;
}

export interface ChangeWorkGroupContact {
  id: number;
  name: string;
  email: string;
}

export interface ChangeWorkGroupInternalTemplate {
  id: number;
  name: string;
  default_title?: string | null;
  field_count?: number;
  show_on_dispatch?: boolean;
  owner_user_id?: number | null;
  is_personal?: boolean;
}

export interface ChangeWorkGroup {
  group_id: number;
  name?: string | null;
  description?: string | null;
  is_active: boolean;
  auto_subtask_on_normal?: boolean;
  auto_subtask_allow_thread?: boolean;
  show_on_dispatch?: boolean;
  open_in_incidents?: boolean;
  incidents_group_id?: number | null;
  incidents_workflow_id?: number | null;
  prompt_on_create?: boolean;
  incident_attachment_allowed?: boolean;
  incident_title_default?: string | null;
  incident_description_default?: string | null;
  incident_urgency_default?: string | null;
  incident_custom_field_defaults?: Record<string, unknown> | null;
  incident_kind?: 'si' | 'infra_nuvem' | 'infra_hosting' | string | null;
  checklist_field_count?: number;
  internal_subtask_template_id?: number | null;
  internal_subtask_template_name?: string | null;
  internal_templates?: ChangeWorkGroupInternalTemplate[];
  review_key?: string;
  members: ChangeWorkGroupMember[];
  contacts?: ChangeWorkGroupContact[];
}

export interface ChangeWorkGroupsExportPayload {
  version: string;
  module: string;
  exported_at: string;
  groups: ChangeWorkGroupExportItem[];
}

export interface ChangeWorkGroupExportItem {
  name: string;
  description?: string | null;
  is_active: boolean;
  members: Array<{ user_email: string; is_manager: boolean }>;
  contacts: Array<{ name: string; email: string }>;
  auto_subtask_on_normal: boolean;
  auto_subtask_allow_thread: boolean;
  internal_templates: Array<{ template_name: string; show_on_dispatch: boolean }>;
  open_in_incidents: boolean;
  incidents_group_name?: string | null;
  incidents_workflow_name?: string | null;
  prompt_on_create: boolean;
  incident_attachment_allowed: boolean;
  incident_title_default?: string | null;
  incident_description_default?: string | null;
  incident_urgency_default?: string | null;
  incident_kind?: string | null;
  incident_custom_field_defaults?: Record<string, unknown>;
}

export interface ChangeWorkGroupsImportResponse {
  success: boolean;
  dry_run: boolean;
  created: number;
  updated: number;
  errors: number;
  results: Array<{ name: string; action: string; message?: string | null }>;
}

export interface InternalSubtaskTemplate {
  id: number;
  name: string;
  default_title?: string | null;
  task_type_id?: number | null;
  task_type_name?: string | null;
  allow_thread_default?: boolean;
  show_on_dispatch?: boolean;
  is_active?: boolean;
  form_tabs?: string[];
  form_tabs_position?: InternalSubtaskFormTabsPosition;
  field_count?: number;
  linked_group_count?: number;
  linked_group_ids?: number[];
  linked_groups?: Array<{ group_id: number; name: string | null }>;
  owner_user_id?: number | null;
  is_personal?: boolean;
  created_at?: string;
  updated_at?: string;
}

export type ChangeChecklistGateStatus = 'autorizar' | 'transporte' | 'revisar' | 'off';

export type ChangeSubtaskGateStatus =
  | 'off'
  | 'avaliar'
  | 'agendado'
  | 'autorizar'
  | 'transporte'
  | 'implementar'
  | 'revisar';

export interface ChangeJiraLink {
  id: number;
  issue_key: string;
  issue_summary?: string | null;
  issue_status?: string | null;
  issue_url?: string | null;
}

/** Referência a um cadastro de apoio (categoria, área solicitante). */
export interface ChangeCatalogRef {
  id: number;
  name: string;
}

/** Registro completo do cadastro, usado nas telas de CRUD. */
export interface ChangeCatalogItem extends ChangeCatalogRef {
  description?: string | null;
  is_active: boolean;
}

export interface ChangeCatalogPayload {
  name: string;
  description?: string | null;
  is_active?: boolean;
}

/** Aba do hub de cadastros (GET /api/changes/catalogs). */
export interface ChangeCatalogKindItem {
  slug: string;
  kind:
    | 'simple'
    | 'work_groups'
    | 'committees'
    | 'emergency_policy'
    | 'sap_request_transport'
    | 'approval_settings'
    | 'approval_form'
    | 'create_form'
    | 'detail_summary'
    | 'subtask_form'
    | 'subtask_requester_policy'
    | 'post_impl_form'
    | 'detail_tabs'
    | 'cab_slides'
    | 'change_types'
    | 'change_email_triggers'
    | 'change_email_queued'
    | 'agenda_email'
    | 'internal_auto_subtasks'
    | 'internal_subtask_templates';
  label: string;
  plural_label: string;
  title: string;
  description: string;
  path: string;
  tab: string;
}

/** Item slim da listagem GET /api/changes/ (sem nested pesado). */
export interface ChangeListItem {
  id: number;
  title: string;
  project: string;
  category_id: number;
  category?: ChangeCatalogRef | null;
  /** Janela de Go Live: início e fim. */
  go_live_start_at: string;
  go_live_end_at: string;
  /** Agendamentos de governança — só data, sem hora. */
  risk_committee_start_at?: string | null;
  risk_committee_end_at?: string | null;
  camud_go_no_go_date?: string | null;
  camud_extraordinary?: boolean;
  change_type: ChangeType;
  change_owner_id: number;
  requester_id: number;
  requester_area_id: number;
  requester_area?: ChangeCatalogRef | null;
  status: ChangeStatus | string;
  /** Backend: status configurável + vínculo do usuário. */
  can_edit_form?: boolean;
  /** Avançar status / operações de mutação da mudança (não inclui decidir aprovação). */
  can_mutate_change?: boolean;
  priority?: string | null;
  current_step?: string | null;
  owner?: ChangeUserLite | null;
  requester?: ChangeUserLite | null;
  created_at: string;
  updated_at: string;
}

export interface ChangeRequest extends ChangeListItem {
  change_type_label?: string;
  change_type_policy?: ChangeTypePolicyFlags;
  created_by?: number | null;
  sap_classification?: 'sap' | 'nao_sap' | string | null;
  sap_has_request?: boolean | null;
  description: string;
  scope?: string | null;
  approval_mode?: 'standard' | 'emergency';
  emergency_approval?: ChangeEmergencyPreview | null;
  requester_manager_group_id?: number | null;
  requester_manager_group_name?: string | null;
  requester_immediate_manager_display?: string | null;
  requester_immediate_manager_setup?: RequesterImmediateManagerSetup | null;
  impacted_users: string;
  rollback_plan: string;
  implementation_result?: string | null;
  had_incidents?: boolean | null;
  change_executed?: boolean | null;
  change_executed_successfully?: boolean | null;
  rollback_executed?: boolean | null;
  can_view_post_implementation?: boolean;
  can_edit_post_implementation?: boolean;
  approvals: ChangeApproval[];
  approval_plan?: ChangeApprovalPlan;
  approval_settings?: ChangeApprovalSettings;
  extra_fields?: Record<string, string | null>;
  date_revisions?: Record<string, ChangeDateRevision>;
  post_impl_extra_fields?: Record<string, string | null>;
  subtasks: ChangeSubtask[];
  jira_links: ChangeJiraLink[];
  can_create_subtask?: boolean;
  /** Gate Infra (Transporte/Implementar): gestor, dono ou solicitante — não aprovador de área. */
  can_open_implementation_gate?: boolean;
  /** Apresentação CAB: gestor, criador/solicitante ou colega de grupo (flag). */
  can_edit_cab_deck?: boolean;
  subtask_requester_defaults?: ChangeSubtaskRequesterDefaults;
}

export type ChangeFormFieldType =
  | 'text_short'
  | 'text_long'
  | 'integer'
  | 'decimal'
  | 'currency'
  | 'date'
  | 'datetime'
  | 'period'
  | 'period_datetime'
  | 'list'
  | 'yes_no'
  | 'immediate_manager_pick';

export type ChangeFormFieldKind = 'native' | 'extra';

export type ChangePeriodMode = 'range' | 'start' | 'end' | 'optional_range';

/** Gatilhos por valor de opção em campos lista (formulário de criação). */
export interface ChangeFormFieldOptionRule {
  label: string;
  enable_internal_subtasks?: boolean;
  enable_external_subtasks?: boolean;
  skips_auto_checklist?: boolean;
  enable_committee_approvals?: boolean;
  require_immediate_manager_transport?: boolean;
  show_field_ids?: number[];
}

/** Histórico de um campo de data/data-hora com limite configurável. */
export interface ChangeDateRevision {
  limit: number;
  used: number;
  remaining: number;
  locked: boolean;
  previous: string[];
  previous_labels: string[];
}

export interface ChangeFormField {
  id: number;
  kind: ChangeFormFieldKind;
  native_key?: string | null;
  label: string;
  field_type: ChangeFormFieldType;
  period_mode?: ChangePeriodMode | null;
  weekday_constraint?: number | null;
  /** Quantas vezes a data pode ser trocada depois da criação. Null = livre. */
  date_revision_limit?: number | null;
  is_required: boolean;
  help_text?: string | null;
  options: string[];
  /** Regras enriquecidas por opção (paralelo a options quando persistido no backend). */
  option_rules?: ChangeFormFieldOptionRule[];
  /** Quando true, este campo lista governa subtarefas/aprovações na criação. */
  is_flow_trigger_source?: boolean;
  sort_order: number;
  col_span: 6 | 12 | number;
  section_name?: string | null;
  is_visible: boolean;
  detail_summary_visible?: boolean;
  detail_summary_sort_order?: number;
  is_locked: boolean;
  skips_auto_checklist?: boolean;
  /** Campo tipo lista / solicitante / dono: exibe filtro de pesquisa no select. */
  select_search_enabled?: boolean;
  conditional_field_id?: number | null;
  conditional_operator?: string | null;
  conditional_value?: string | null;
  default_value?: string | null;
  /** Chave estável do catálogo global (ex.: planned_start). */
  preset_spec_key?: string | null;
  respondent_role?: 'requester' | 'assignee' | null;
  is_system_preset?: boolean;
  /** Pode excluir no cadastro do formulário (nativos críticos vêm com motivo em delete_block_reason). */
  can_delete?: boolean;
  delete_block_reason?: string | null;
}

export interface InternalSubtaskPresetField extends ChangeFormField {
  spec_key: string;
  is_active?: boolean;
}

export interface InternalSubtaskListLayoutColumn {
  column_key: string;
  label: string;
  group: string;
  sort_order: number;
  is_visible: boolean;
}

export interface InternalSubtaskListLayoutResponse {
  columns: InternalSubtaskListLayoutColumn[];
  preset_fields: InternalSubtaskPresetField[];
}

/** Resposta de GET/PUT do schema + políticas do formulário de subtarefa. */
export interface ChangeSubtaskFormSchemaResponse {
  items: ChangeFormField[];
  conclude_mode: ChangeSubtaskConcludeMode;
  conclude_button_label: string;
  allow_create_attachments: boolean;
  checklist_required_before_status: ChangeChecklistGateStatus;
  internal_subtask_gate_before_status?: ChangeSubtaskGateStatus;
  external_subtask_gate_before_status?: ChangeSubtaskGateStatus;
  requester_default_allow_create?: boolean;
  requester_default_allow_edit?: boolean;
  requester_default_allow_delete?: boolean;
  package_create_statuses?: string[];
}

export type ChangeExtraValue =
  | string
  | { start: string; end: string }
  | { currency: string; value: string }
  | null;

export type ChangeExtraFields = Record<string, ChangeExtraValue>;

export interface ChangeListResponse {
  items: ChangeListItem[];
  total: number;
  skip: number;
  limit: number;
}

/** Feriado da tabela `holidays` no recorte da grade. */
export interface ChangeCalendarHoliday {
  date: string;
  name: string;
}

/** Item da grade do calendário (GET /api/changes/calendar) — projeção enxuta. */
export interface ChangeCalendarItem {
  id: number;
  title: string;
  status: string;
  change_type: string;
  go_live_start_at: string;
  go_live_end_at: string;
  risk_committee_start_at?: string | null;
  risk_committee_end_at?: string | null;
  camud_go_no_go_date?: string | null;
  camud_extraordinary?: boolean;
  category_id?: number;
  category?: ChangeCatalogRef | null;
  project: string;
  owner_id?: number;
}

export type ChangeMeetingKind = 'riscos_impactos' | 'camud_go_no_go' | 'outros';
export type ChangeMeetingStatus = 'agendada' | 'em_andamento' | 'encerrada';

export interface ChangeMeetingLite {
  id: number;
  kind: ChangeMeetingKind | string;
  kind_label: string;
  title: string;
  scheduled_at: string;
  countdown_seconds: number;
  status: ChangeMeetingStatus | string;
  status_label: string;
  item_count: number;
  total_elapsed_seconds?: number;
}

export interface ChangeMeetingItem {
  id: number;
  change_id: number;
  sort_order: number;
  elapsed_seconds: number;
  running: boolean;
  title: string;
  status?: string | null;
  status_label?: string | null;
  change_type?: string | null;
  project?: string | null;
  fields: Record<string, string>;
}

export interface ChangeMeetingAgendaEmailPreviewItem {
  id: number;
  title: string;
  status?: string | null;
  status_label?: string | null;
  change_type?: string | null;
  owner_name?: string | null;
}

export interface ChangeMeetingAgendaEmailPreview {
  meeting_id: number;
  meeting_title: string;
  meeting_scheduled_at: string;
  meeting_kind?: string | null;
  meeting_kind_label?: string | null;
  agenda_kind: AgendaKind | string;
  layout_key: string;
  layout_label: string;
  layout_enabled: boolean;
  layout_source?: string | null;
  subject: string;
  body_html: string;
  template_subject: string;
  template_body_html: string;
  pauta_html: string;
  items: ChangeMeetingAgendaEmailPreviewItem[];
  change_count: number;
  catalog_tab?: string;
}

export interface ChangeMeeting {
  id: number;
  kind: ChangeMeetingKind | string;
  kind_label: string;
  title: string;
  scheduled_at: string;
  countdown_seconds: number;
  status: ChangeMeetingStatus | string;
  status_label: string;
  current_item_id?: number | null;
  closed_at?: string | null;
  items: ChangeMeetingItem[];
  total_elapsed_seconds: number;
  item_count: number;
}

export interface ChangeCabSlide {
  id: number;
  title: string;
  field_keys: string[];
  sort_order: number;
  is_active: boolean;
}

export interface ChangeCabSlideField {
  key: string;
  label: string;
}

export type ChangeCabDeckElementType = 'label' | 'field' | 'text' | 'image';

export interface ChangeCabDeckImageCrop {
  /** Região da imagem original mantida (0–100%). */
  xPct: number;
  yPct: number;
  wPct: number;
  hPct: number;
}

export interface ChangeCabDeckElementStyle {
  fontWeight?: number;
  fontSizeVw?: number;
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
  objectFit?: 'contain' | 'cover' | 'fill';
  crop?: ChangeCabDeckImageCrop;
  rail?: boolean;
}

export interface ChangeCabDeckElement {
  id?: number;
  /** Chave estável só no editor (não persiste). */
  clientKey?: string;
  slide_id?: number;
  sort_order: number;
  type: ChangeCabDeckElementType;
  field_key?: string | null;
  content?: string | null;
  style?: ChangeCabDeckElementStyle;
  x_pct: number;
  y_pct: number;
  w_pct: number;
  h_pct: number;
}

/** Dados estruturados de templates CCO-531 (conclusão, riscos, brand). */
export type ChangeCabDeckSlideLayout = Record<string, unknown>;

export interface ChangeCabDeckSlide {
  id?: number;
  deck_id?: number;
  title: string;
  sort_order: number;
  is_active: boolean;
  /** Serializado pelo BE como `layout` (coluna layout_json). */
  layout?: ChangeCabDeckSlideLayout | null;
  elements: ChangeCabDeckElement[];
}

export interface ChangeCabDeckTheme {
  bg: string;
  blue: string;
  green: string;
  text: string;
  chromeBg: string;
  chromeText: string;
  chromeAccent: string;
  titleFontFamily: string;
  titleFontSizeVw: number;
  titleFontWeight: number;
  dateFontSizeRem: number;
  capaFixedTitle: string;
  useChangeTitleOnCapa: boolean;
}

export interface ChangeCabDeck {
  id: number;
  scope: 'global' | 'change';
  change_id?: number | null;
  /** Criador da mudança (só scope=change) — usado para AuthZ de edição do deck próprio. */
  change_created_by?: number | null;
  /** Prévia sem materializar clone no banco (viewer sem deck local). */
  is_readonly_preview?: boolean;
  can_edit_cab_deck?: boolean;
  theme: ChangeCabDeckTheme;
  slides: ChangeCabDeckSlide[];
  fields: ChangeCabSlideField[];
  /** Valores reais da mudança para prévia no editor (somente scope=change). */
  preview_values?: Record<string, string>;
}

/** Item da pauta (GET /api/changes/agenda) — a categoria já vem resolvida. */
export interface ChangeAgendaEmailVariable {
  token: string;
  key: string;
  group: string;
  group_label: string;
  label: string;
  description: string;
  example: string;
  aliases?: string;
}

export interface ChangeAgendaItem {
  id: number;
  title: string;
  description?: string | null;
  status: string;
  change_type: string;
  category?: string | null;
  project: string;
  go_live_start_at: string;
  go_live_end_at: string;
  risk_committee_start_at?: string | null;
  risk_committee_end_at?: string | null;
  camud_go_no_go_date?: string | null;
  camud_extraordinary?: boolean;
  owner_id?: number;
  owner_name?: string | null;
  agenda_section?: string;
}

export type AgendaKind = 'committee' | 'camud' | 'both';

export type AgendaLayoutKey =
  | 'auto'
  | 'global'
  | 'riscos_impactos'
  | 'camud_go_no_go'
  | 'camud_extraordinario'
  | 'custom';

export function defaultLayoutForAgendaKind(kind: AgendaKind): AgendaLayoutKey {
  if (kind === 'committee') return 'riscos_impactos';
  if (kind === 'camud') return 'camud_go_no_go';
  return 'global';
}

export interface ChangeCommittee {
  id: number;
  name: string;
  kind: string;
  kind_label: string;
  is_active: boolean;
  is_system: boolean;
  people_count?: number;
}

export interface ChangeCommitteePerson {
  id: number;
  committee_id: number;
  committee_name?: string | null;
  committee_kind?: string | null;
  name: string;
  email: string;
  receives_email: boolean;
}

export interface ChangeAgendaEmailTemplate {
  id?: number | null;
  key?: AgendaLayoutKey | string;
  label?: string;
  scope: 'global' | 'committee';
  committee_id?: number | null;
  enabled: boolean;
  subject: string;
  body_html: string;
  is_default?: boolean;
  default_for?: AgendaKind[];
  variables?: ChangeAgendaEmailVariable[];
}

export interface ChangeEmailTriggerExtraGroup {
  group_id: number;
  /** null = todos os membros ativos do grupo */
  member_user_ids?: number[] | null;
  /** null = todos os contatos do grupo */
  contact_ids?: number[] | null;
}

export interface ChangeEmailTriggerOptions {
  internal_recipient_scope?: 'work_group_all' | 'assignee_only';
  /** Gatilho internal_subtask_status_changed */
  watch_subtask_statuses?: string[];
  send_to_admins?: boolean;
  send_to_change_managers?: boolean;
}

export interface ChangeEmailTriggerConfig {
  trigger_key: string;
  label: string;
  enabled: boolean;
  configured?: boolean;
  send_to_owner: boolean;
  send_to_requester: boolean;
  send_to_creator: boolean;
  extra_user_ids: number[];
  extra_groups?: ChangeEmailTriggerExtraGroup[];
  subject_template: string;
  body_html_template: string;
  /** Somente gatilho status_changed: status que disparam e-mail ao entrar neles. */
  watch_statuses?: string[];
  /** Gatilhos internal_subtask_created / internal_subtask_canceled. */
  trigger_options?: ChangeEmailTriggerOptions;
  updated_at?: string | null;
}

export interface ChangeEmailTriggerQueuedItem {
  queued_id: number;
  change_id: number;
  send_at: string | null;
  status: string;
}

export interface ChangeEmailQueueItem {
  kind: 'agenda' | 'trigger';
  queued_id: number;
  send_at: string | null;
  label: string;
  reference: string;
}

export interface ChangeAgendaEmailSchedule {
  id?: number;
  agenda_kind: 'committee' | 'camud';
  enabled: boolean;
  offset_days: number;
  preset?: 'one_day' | 'three_days' | 'custom';
  send_time: string;
  pauta_weekday?: number;
  pauta_weekday_label?: string;
}

export interface ChangeAgendaRecipient {
  kind?: 'person' | 'user' | 'contact';
  person_id?: number | null;
  user_id?: number | null;
  contact_id?: number | null;
  name: string;
  email: string;
  committee_id?: number;
  committee_name?: string | null;
  committee_kind?: string | null;
  group_id?: number;
  group_name?: string | null;
  track?: string;
  receives_email?: boolean;
}

export interface ChangeAgendaRecipientCommittee {
  id: number;
  name: string;
  kind: string;
  kind_label: string;
}

export interface ChangeAgendaPreview {
  committee_agenda: ChangeAgendaItem[];
  camud_agenda: ChangeAgendaItem[];
}

export type ChangeAgendaSendKind = Exclude<AgendaKind, 'both'>;

export interface ChangeAgendaSendSummaryItem {
  agenda_kind: ChangeAgendaSendKind;
  agenda_kind_label: string;
  last_sent_at: string | null;
  send_log_id: number;
  recipients_count: number;
}

export interface ChangeAgendaQueuedSendSummaryItem {
  queued_id: number;
  send_at: string | null;
  agenda_kind: string;
  agenda_kind_label: string;
  status: string;
}

export interface ChangeAgendaSendSummary {
  change_id: number;
  items: ChangeAgendaSendSummaryItem[];
  queued_items?: ChangeAgendaQueuedSendSummaryItem[];
}

export interface ChangeMeetingAgendaSendSummary {
  meeting_id: number;
  items: ChangeAgendaSendSummaryItem[];
  queued_items?: ChangeAgendaQueuedSendSummaryItem[];
}

/** Registro do histórico de envios de pauta (GET /api/changes/agenda/sends). */
export interface ChangeAgendaSend {
  id: number;
  created_at: string;
  trigger_source: string;
  sent_by?: { id: number; name: string } | null;
  agenda_kind: string;
  agenda_kind_label: string;
  window_start: string;
  window_end: string;
  outcome: string;
  recipients_count: number;
  changes_count: number;
  recipients: string[];
  subject?: string | null;
  items: Array<Record<string, unknown>>;
  body_html?: string | null;
}

export type ChangeAssistantChatRole = 'user' | 'assistant';

export interface ChangeAssistantHistoryMessage {
  role: ChangeAssistantChatRole;
  content: string;
}

export interface ChangeAssistantResponse {
  answer: string;
  generated_at: string;
}

export type ChangeDetailTabFieldType =
  | 'text_short'
  | 'text_long'
  | 'integer'
  | 'decimal'
  | 'list'
  | 'yes_no'
  | 'user_picker';

export interface ChangeDetailTabField {
  id: number;
  tab_id: number;
  field_key: string;
  label: string;
  field_type: ChangeDetailTabFieldType;
  is_required: boolean;
  help_text?: string | null;
  options: string[];
  is_searchable: boolean;
  sort_order: number;
  is_visible: boolean;
}

export interface ChangeDetailPageTabSlot {
  key: string;
  kind: 'fixed' | 'custom';
  label: string;
  tab_id?: number;
}

export interface ChangeDetailPageTabLayoutResponse {
  order: string[];
  slots: ChangeDetailPageTabSlot[];
}

export interface ChangeDetailTabDefinition {
  id: number;
  slug: string;
  label: string;
  is_visible: boolean;
  sort_order: number;
  visible_statuses: string[];
  enable_create: boolean;
  enable_pagination: boolean;
  enable_search: boolean;
  fields: ChangeDetailTabField[];
  entries_count?: number;
}

export interface ChangeDetailTabEntrySlim {
  id: number;
  tab_id: number;
  change_request_id: number;
  created_at: string;
  updated_at: string;
  display: Record<string, string>;
}

export interface ChangeDetailTabEntryDetail {
  id: number;
  tab_id: number;
  change_request_id: number;
  fields: Array<{
    field_id: number;
    field_key: string;
    label: string;
    field_type: ChangeDetailTabFieldType;
    value?: string | null;
    user_id?: number;
    user_label?: string | null;
  }>;
  created_at: string;
  updated_at: string;
  created_by: { id: number; name?: string | null };
  updated_by?: { id: number; name?: string | null } | null;
}
